import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, History } from 'lucide-react';
import type {
  OrganizationSport,
  PersonSportRating,
  PersonSportRatingHistoryEntry,
  PlayerRatingSettings,
} from '@court-sync/shared';
import { formatPlayerRating } from '@court-sync/shared';
import {
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Switch,
  Textarea,
  Modal,
} from '@/components/ui';
import { AcademySettingsSection } from '@/components/academy/AcademySettingsSection';
import { SportIconTile } from '@/components/sport/SportIcons';
import { playerRatingService } from '@/services/playerRating';
import { organizationService } from '@/services/organization';
import { getAcademySportLabel } from '@/lib/sportPresentation';
import { PlayerRatingHistoryTimeline } from './PlayerRatingHistoryTimeline';

type RatingDraft = {
  rating: string;
  notes: string;
};

export type PendingSportRating = {
  organizationSportId: string;
  rating: number;
  notes?: string | null;
};

export interface PersonSportRatingsEditorProps {
  settings: PlayerRatingSettings;
  sports: OrganizationSport[];
  currentRatings: PersonSportRating[];
  onSaved: () => void;
  loadHistory: (organizationSportId: string) => Promise<PersonSportRatingHistoryEntry[]>;
  saveRatings: (payload: {
    organizationSportId: string;
    rating: number;
    notes?: string | null;
  }[]) => Promise<void>;
  compact?: boolean;
  draftMode?: boolean;
  onDraftsChange?: (ratings: PendingSportRating[]) => void;
}

export function PersonSportRatingsEditor({
  settings,
  sports,
  currentRatings,
  onSaved,
  loadHistory,
  saveRatings,
  compact = false,
  draftMode = false,
  onDraftsChange,
}: PersonSportRatingsEditorProps) {
  const activeSports = useMemo(() => sports.filter((sport) => sport.isActive), [sports]);
  const ratingBySportId = useMemo(
    () => new Map(currentRatings.map((row) => [row.organizationSportId, row.rating])),
    [currentRatings]
  );

  const baseDrafts = useMemo(() => {
    const next: Record<string, RatingDraft> = {};
    for (const sport of activeSports) {
      const current = ratingBySportId.get(sport.id);
      next[sport.id] = {
        rating: current != null ? String(current) : '',
        notes: '',
      };
    }
    return next;
  }, [activeSports, ratingBySportId]);

  const [draftEdits, setDraftEdits] = useState<Record<string, Partial<RatingDraft>>>({});
  const drafts = useMemo(() => {
    const merged = { ...baseDrafts };
    for (const [sportId, edit] of Object.entries(draftEdits)) {
      merged[sportId] = { ...merged[sportId], ...edit };
    }
    return merged;
  }, [baseDrafts, draftEdits]);

  const updateDraft = (sportId: string, patch: Partial<RatingDraft>) => {
    setDraftEdits((prev) => ({
      ...prev,
      [sportId]: { ...prev[sportId], ...patch },
    }));
  };

  const [savingSportId, setSavingSportId] = useState<string | null>(null);
  const [historySportId, setHistorySportId] = useState<string | null>(null);
  const [historySportLabel, setHistorySportLabel] = useState<string | null>(null);
  const [historyEntries, setHistoryEntries] = useState<PersonSportRatingHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedSportId, setExpandedSportId] = useState<string | null>(null);

  useEffect(() => {
    if (compact && activeSports.length > 0 && expandedSportId == null) {
      setExpandedSportId(activeSports[0].id);
    }
  }, [activeSports, compact, expandedSportId]);

  useEffect(() => {
    if (!draftMode || !onDraftsChange) return;
    const pending: PendingSportRating[] = [];
    for (const sport of activeSports) {
      const draft = drafts[sport.id];
      if (!draft?.rating.trim()) continue;
      const rating = Number(draft.rating);
      if (!Number.isFinite(rating)) continue;
      pending.push({
        organizationSportId: sport.id,
        rating,
        notes: draft.notes.trim() ? draft.notes.trim() : null,
      });
    }
    onDraftsChange(pending);
  }, [activeSports, draftMode, drafts, onDraftsChange]);

  const handleSaveSport = async (organizationSportId: string) => {
    const draft = drafts[organizationSportId];
    if (!draft?.rating.trim()) {
      toast.error('Enter a rating value');
      return;
    }
    const rating = Number(draft.rating);
    if (!Number.isFinite(rating)) {
      toast.error('Rating must be a number');
      return;
    }

    setSavingSportId(organizationSportId);
    try {
      await saveRatings([
        {
          organizationSportId,
          rating,
          notes: draft.notes.trim() ? draft.notes.trim() : null,
        },
      ]);
      toast.success(`${settings.label} saved`);
      setDraftEdits((prev) => {
        const next = { ...prev };
        delete next[organizationSportId];
        return next;
      });
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save rating');
    } finally {
      setSavingSportId(null);
    }
  };

  const openHistory = useCallback(async (sport: OrganizationSport) => {
    setHistorySportId(sport.id);
    setHistorySportLabel(getAcademySportLabel(sport));
    setHistoryLoading(true);
    try {
      const entries = await loadHistory(sport.id);
      setHistoryEntries(entries);
    } catch {
      toast.error('Failed to load rating history');
      setHistoryEntries([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [loadHistory]);

  const closeHistory = useCallback(() => {
    setHistorySportId(null);
    setHistorySportLabel(null);
    setHistoryEntries([]);
    setHistoryLoading(false);
  }, []);

  if (!settings.enabled) {
    return null;
  }

  if (activeSports.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Add active sports in Organization settings to set per-sport ratings.
      </p>
    );
  }

  return (
    <>
      <div className={compact ? 'space-y-2' : 'space-y-4'}>
      {activeSports.map((sport) => {
        const draft = drafts[sport.id] ?? { rating: '', notes: '' };
        const label = getAcademySportLabel(sport);
        const currentRating = ratingBySportId.get(sport.id);
        const isExpanded = expandedSportId === sport.id;
        const hasCurrent = currentRating != null;

        return (
          <div
            key={sport.id}
            className={
              compact
                ? 'overflow-hidden rounded-lg border border-border/80 bg-background shadow-sm'
                : 'rounded-lg border border-border p-4 space-y-3'
            }
          >
            <button
              type="button"
              className={
                compact
                  ? 'flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40'
                  : 'flex w-full items-center justify-between gap-2'
              }
              onClick={() => setExpandedSportId(isExpanded ? null : sport.id)}
              aria-expanded={isExpanded}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <SportIconTile sportId={sport.sportId} className="h-8 w-8 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{label}</p>
                  {hasCurrent ? (
                    <p className="text-xs text-muted-foreground">
                      Current {formatPlayerRating(currentRating)}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">No rating set</p>
                  )}
                </div>
              </div>
              {compact ? (
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              ) : null}
            </button>

            {(!compact || isExpanded) ? (
              <div className={compact ? 'space-y-3 border-t border-border/60 px-3 pb-3 pt-2' : 'space-y-3'}>
                <div className={compact ? 'space-y-3' : 'grid gap-3 sm:grid-cols-2'}>
                  <Field>
                    <FieldLabel htmlFor={`rating-${sport.id}`}>{settings.label}</FieldLabel>
                    <Input
                      id={`rating-${sport.id}`}
                      type="number"
                      step={settings.step}
                      min={settings.min}
                      max={settings.max}
                      value={draft.rating}
                      onChange={(e) => updateDraft(sport.id, { rating: e.target.value })}
                      placeholder={`${settings.min} – ${settings.max}`}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`notes-${sport.id}`}>Notes (optional)</FieldLabel>
                    <Textarea
                      id={`notes-${sport.id}`}
                      rows={compact ? 2 : 2}
                      value={draft.notes}
                      onChange={(e) => updateDraft(sport.id, { notes: e.target.value })}
                      placeholder="e.g. Post-tournament adjustment"
                    />
                  </Field>
                </div>
                {!draftMode ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={savingSportId === sport.id}
                      onClick={() => void handleSaveSport(sport.id)}
                    >
                      {savingSportId === sport.id ? 'Saving…' : 'Save rating'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => void openHistory(sport)}
                    >
                      <History className="h-3.5 w-3.5" aria-hidden />
                      History
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      </div>

      <Modal
        isOpen={historySportId != null}
        onClose={closeHistory}
        title={
          historySportLabel
            ? `${settings.label} history · ${historySportLabel}`
            : `${settings.label} history`
        }
        stackLevel="elevated"
        maxWidth="md"
      >
        {historyLoading ? (
          <p className="text-sm text-muted-foreground">Loading history…</p>
        ) : (
          <PlayerRatingHistoryTimeline entries={historyEntries} ratingLabel={settings.label} />
        )}
      </Modal>
    </>
  );
}

interface OrgPlayerRatingSettingsTabProps {
  organizationId: string;
  formTourId?: string;
}

export function OrgPlayerRatingSettingsTab({
  organizationId,
  formTourId,
}: OrgPlayerRatingSettingsTabProps) {
  const [settings, setSettings] = useState<PlayerRatingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await playerRatingService.getSettings(organizationId);
      if (res.success && res.data) {
        setSettings(res.data);
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await playerRatingService.updateSettings(organizationId, settings);
      if (res.success && res.data) {
        setSettings(res.data);
        toast.success('Player rating settings saved');
      } else {
        toast.error(res.error ?? 'Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div
        id={formTourId}
        className="flex min-h-[120px] items-center justify-center"
        aria-busy="true"
      >
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  return (
    <AcademySettingsSection
      title="Player ratings"
      description="Internal ratings for matchmaking and tournaments. Not shown to players on public boards or member portals."
      className="scroll-mt-4"
    >
      <div
        id={formTourId}
        className="max-w-2xl space-y-5 rounded-xl border border-violet-200/80 bg-violet-50/30 p-5 dark:border-violet-900/40 dark:bg-violet-950/15"
      >
        <Field>
          <div className="flex items-center justify-between gap-4">
            <div>
              <FieldLabel>Enable player ratings</FieldLabel>
              <FieldDescription>
                Operators can set per-sport ratings on members and students.
              </FieldDescription>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings((prev) => (prev ? { ...prev, enabled: checked } : prev))}
              aria-label="Enable player ratings"
            />
          </div>
        </Field>

        <Field>
          <FieldLabel htmlFor="player-rating-label">Label</FieldLabel>
          <Input
            id="player-rating-label"
            value={settings.label}
            onChange={(e) => setSettings((prev) => (prev ? { ...prev, label: e.target.value } : prev))}
          />
          <FieldDescription>Shown to operators only (e.g. &quot;Player rating&quot;, &quot;Club level&quot;).</FieldDescription>
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field>
            <FieldLabel htmlFor="player-rating-min">Minimum</FieldLabel>
            <Input
              id="player-rating-min"
              type="number"
              step="0.1"
              value={settings.min}
              onChange={(e) =>
                setSettings((prev) => (prev ? { ...prev, min: Number(e.target.value) } : prev))
              }
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="player-rating-max">Maximum</FieldLabel>
            <Input
              id="player-rating-max"
              type="number"
              step="0.1"
              value={settings.max}
              onChange={(e) =>
                setSettings((prev) => (prev ? { ...prev, max: Number(e.target.value) } : prev))
              }
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="player-rating-step">Step</FieldLabel>
            <Input
              id="player-rating-step"
              type="number"
              step="0.1"
              value={settings.step}
              onChange={(e) =>
                setSettings((prev) => (prev ? { ...prev, step: Number(e.target.value) } : prev))
              }
            />
          </Field>
        </div>

        <p className="rounded-lg bg-background/80 px-3 py-2 text-sm text-muted-foreground">
          Example value on this scale: <span className="font-medium text-foreground">{formatPlayerRating((settings.min + settings.max) / 2)}</span>
        </p>

        <div className="flex justify-end">
          <Button type="button" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>
    </AcademySettingsSection>
  );
}

export async function loadOrganizationSportsForRatings(organizationId: string) {
  const res = await organizationService.getOrganizationSports(organizationId);
  return res.success && res.data ? res.data : [];
}
